<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <div>
      <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
      <input 
        type="text" 
        id="name" 
        v-model="formData.name" 
        required 
        class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-primary focus:border-primary sm:text-sm"
      >
    </div>
    <div>
      <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
      <input 
        type="email" 
        id="email" 
        v-model="formData.email" 
        required 
        class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-primary focus:border-primary sm:text-sm"
      >
    </div>
    <div>
      <label for="message" class="block text-sm font-medium text-gray-700">Message / Comments</label>
      <textarea 
        id="message" 
        rows="4" 
        v-model="formData.message" 
        required
        class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-primary focus:border-primary sm:text-sm"
      ></textarea>
      <p class="mt-1 text-xs text-gray-500">Please include any questions you have about the selected models.</p>
    </div>

    <!-- Optional: Hidden field or similar to include model info -->
    <!-- <input type="hidden" :value="JSON.stringify(modelIds)"> -->

    <div>
      <button 
        type="submit" 
        :disabled="isSubmitting" 
        class="inline-flex justify-center py-2 px-4 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
      >
        {{ isSubmitting ? 'Submitting...' : 'Submit Enquiry' }}
      </button>
    </div>

    <div v-if="submitStatus === 'success'" class="mt-4 p-3 rounded-md bg-green-50 border border-green-300 text-green-700">
      Enquiry submitted successfully! We will be in touch soon.
    </div>
    <div v-if="submitStatus === 'error'" class="mt-4 p-3 rounded-md bg-red-50 border border-red-300 text-red-700">
      Failed to submit enquiry. Please try again later.
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import type { PropType } from 'vue'
import { useFavoritesStore, type FavoriteItem } from '~/stores/favorites' // Import FavoriteItem

const props = defineProps({
  favorites: {
    type: Array as PropType<FavoriteItem[]>,
    required: true,
  },
})

const formData = reactive({
  name: '',
  email: '',
  message: '',
})

const isSubmitting = ref(false)
const submitStatus = ref<'idle' | 'success' | 'error'>('idle')

// Prepare detailed model info including comments and flags for submission
const enquiryItems = computed(() => { // Renamed for clarity
  // We already have the necessary data in props.favorites
  // We might want to add the model name back in if needed for the API call
  // This depends on whether the backend can look up names by ID
  // Example adding name back (requires modelsStore lookup):
  /* 
  const modelsStore = useModelsStore() // Would need import
  return props.favorites.map(fav => ({
    ...fav, // id, comment, wantsTestDrive, hasTradeIn, interestedInFinance
    name: modelsStore.models.find(m => m.id === fav.id)?.title.rendered || 'Unknown Model'
  }))
  */
  // For now, just use the raw favorite data passed in props
  // Ensure all fields including testDriveDateTime are included
  return props.favorites.map(fav => ({
    id: fav.id,
    comment: fav.comment,
    wantsTestDrive: fav.wantsTestDrive,
    hasTradeIn: fav.hasTradeIn,
    interestedInFinance: fav.interestedInFinance,
    testDriveDateTime: fav.testDriveDateTime // Ensure this is included
  }))
})

const handleSubmit = async () => {
  isSubmitting.value = true
  submitStatus.value = 'idle'
  console.log('Submitting enquiry for models with options/comments:', enquiryItems.value)
  console.log('Form Data:', formData)

  try {
    // --- Placeholder for actual API call --- 
    // Example: 
    // const response = await $fetch('/api/enquiries', { 
    //   method: 'POST', 
    //   body: { 
    //     ...formData, 
    //     models: enquiryItems.value // Send combined favorite data
    //   }
    // });

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Simulate success
    console.log('Enquiry submitted (simulated).')
    submitStatus.value = 'success'
    // Optionally clear the form
    // formData.name = ''
    // formData.email = ''
    // formData.message = ''

  } catch (error) {
    console.error('Error submitting enquiry:', error)
    submitStatus.value = 'error'
  } finally {
    isSubmitting.value = false
  }
}
</script> 